import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'

import { APP_STATUS } from 'src/constants/common'
import {
  ConnectionDetailType,
  getUserConnectionsByStatus,
  ResponseToConnectionType,
  responseToConnectionRequest,
} from 'src/api/connect'
import { Event, getPendingEvents, responseToEvent, ResponseEvent } from 'src/api/event'
import { RootState } from '../configureStore'

interface RequestsState {
  pendingConnections: ConnectionDetailType[]
  pendingEvents: Event[]
  fetchStatus: APP_STATUS
}

const initialState: RequestsState = {
  pendingConnections: [],
  pendingEvents: [],
  fetchStatus: APP_STATUS.INIT,
}

export const fetchPendingRequests = createAsyncThunk('requests/fetchPending', async () => {
  const [connections, events] = await Promise.all([
    getUserConnectionsByStatus('receiver', 'pending') as Promise<ConnectionDetailType[]>,
    getPendingEvents(),
  ])
  return { connections, events }
})

export const respondToConnection = createAsyncThunk(
  'requests/respondConnection',
  async ({
    connectionId,
    response,
  }: {
    connectionId: string
    response: 'accepted' | 'rejected'
  }) => {
    const data = { response }
    const result = await responseToConnectionRequest(data as ResponseToConnectionType, connectionId)
    // Check for error messages (not 'accepted' or 'Connection request rejected.')
    if (result.message === 'Fail to submit' || result.message === 'Input info invalid') {
      throw new Error(result.message)
    }
    // Check if message is undefined (which shouldn't happen with our fix)
    if (result.message === undefined) {
      throw new Error('Failed to accept request')
    }

    // For both 'accepted' and 'Connection request rejected.', consider it a success
    return { connectionId, response }
  }
)

export const respondToEvent = createAsyncThunk(
  'requests/respondEvent',
  async ({ eventId, response }: { eventId: string; response: 'accepted' | 'rejected' }) => {
    const result = await responseToEvent({ response } as ResponseEvent, eventId)
    if (!('message' in result)) {
      throw new Error('Failed to respond to event')
    }
    return { eventId }
  }
)

const requestsSlice = createSlice({
  name: 'requests',
  initialState,
  reducers: {
    // clear all pending (logout etc)
    resetRequestsState: () => initialState,

    // Manually remove a pending connection (used by TanStack Query mutations)
    removePendingConnection: (state, action: PayloadAction<string>) => {
      state.pendingConnections = state.pendingConnections.filter((c) => c.id !== action.payload)
    },

    // Manually remove a pending event (used by TanStack Query mutations)
    removePendingEvent: (state, action: PayloadAction<string>) => {
      state.pendingEvents = state.pendingEvents.filter((e) => e.id !== action.payload)
    },

    // Update pending requests from TanStack Query (used for global polling)
    updatePendingRequests: (
      state,
      action: PayloadAction<{
        connections: ConnectionDetailType[]
        events: Event[]
      }>
    ) => {
      state.pendingConnections = Array.isArray(action.payload.connections)
        ? action.payload.connections
        : []
      state.pendingEvents = Array.isArray(action.payload.events) ? action.payload.events : []
      state.fetchStatus = APP_STATUS.DONE
    },
  },
  extraReducers: (builder) => {
    /* --------------------------- fetchPending --------------------------- */
    builder
      .addCase(fetchPendingRequests.pending, (state) => {
        state.fetchStatus = APP_STATUS.LOADING
      })
      .addCase(fetchPendingRequests.fulfilled, (state, action) => {
        state.fetchStatus = APP_STATUS.DONE
        state.pendingConnections = Array.isArray(action.payload.connections)
          ? action.payload.connections
          : []
        state.pendingEvents = Array.isArray(action.payload.events) ? action.payload.events : []
      })
      .addCase(fetchPendingRequests.rejected, (state) => {
        state.fetchStatus = APP_STATUS.ERROR
      })
      /* ----------------------- respondToConnection ---------------------- */
      .addCase(respondToConnection.fulfilled, (state, action) => {
        state.pendingConnections = state.pendingConnections.filter(
          (c) => c.id !== action.payload.connectionId
        )
      })
      /* -------------------------- respondEvent -------------------------- */
      .addCase(respondToEvent.fulfilled, (state, action) => {
        state.pendingEvents = state.pendingEvents.filter((e) => e.id !== action.payload.eventId)
      })
  },
})

export const {
  resetRequestsState,
  removePendingConnection,
  removePendingEvent,
  updatePendingRequests,
} = requestsSlice.actions

export const selectPendingConnections = (state: RootState) => state.requests.pendingConnections
export const selectPendingEvents = (state: RootState) => state.requests.pendingEvents
export const selectPendingCount = (state: RootState) =>
  state.requests.pendingConnections.length + state.requests.pendingEvents.length
export const selectFetchStatus = (state: RootState) => state.requests.fetchStatus

export default requestsSlice.reducer
